import type { MetaFunction } from '@remix-run/node';
import { Link } from '@remix-run/react';
import { ArrowLeft } from 'lucide-react';

export const meta: MetaFunction = () => [
  { title: 'Terms of Service — Deleg8 Studio' },
  { name: 'description', content: 'Terms of Service for Deleg8 Studio, the AI-powered creative marketing platform.' },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        <Link
          to="/"
          className="mb-12 inline-flex items-center gap-2 text-sm text-white/60 transition hover:text-white/80"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        <h1 className="text-4xl font-bold">Terms of Service</h1>
        <p className="mt-4 text-sm text-white/60">Last updated: March 6, 2026</p>

        <div className="mt-12 space-y-10 text-sm leading-relaxed text-white/60">
          <section>
            <h2 className="text-lg font-semibold text-white">1. Service Description</h2>
            <p className="mt-3">
              Deleg8 Studio is an AI-powered creative marketing platform that captures brand context and generates multi-channel campaign content. By using Deleg8 Studio, you agree to these terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">2. Accounts</h2>
            <p className="mt-3">
              You must provide accurate information when creating an account. You are responsible for maintaining the security of your account credentials. You must be at least 18 years old to use Deleg8 Studio.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">3. Your Content</h2>
            <p className="mt-3">
              <strong className="text-white">You retain full ownership of all brand materials you upload and all content generated by Deleg8 Studio.</strong> By uploading brand materials, you grant Deleg8 Studio a limited license to process them for the purpose of generating campaign content for your use.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">4. Intellectual Property</h2>
            <p className="mt-3">
              The Deleg8 Studio platform, including its design, code, AI models, and documentation, is owned by Deleg8 Studio. You may not copy, modify, or reverse-engineer the platform. Your use of Deleg8 Studio does not grant you any rights to our intellectual property beyond what is needed to use the service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">5. AI-Generated Content</h2>
            <p className="mt-3">
              Content generated by Deleg8 Studio is produced using AI and should be reviewed before publication. While we strive for accuracy and brand consistency, AI-generated content may contain errors. You are responsible for reviewing and approving all content before use.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">6. Acceptable Use</h2>
            <p className="mt-3">You agree not to:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Use Deleg8 Studio to generate illegal, harmful, or misleading content</li>
              <li>Attempt to reverse-engineer or extract AI models</li>
              <li>Share account credentials with unauthorized users</li>
              <li>Use automated tools to scrape or overload the platform</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">7. Payment</h2>
            <p className="mt-3">
              Paid plans are billed in advance on a monthly or annual basis. Prices may change with 30 days notice. Refunds are handled on a case-by-case basis. Promotional pricing and plan packaging may be modified or discontinued.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">8. Service Availability</h2>
            <p className="mt-3">
              We aim for high availability but do not guarantee uninterrupted service. We may perform maintenance with reasonable notice. We reserve the right to modify, suspend, or discontinue features with notice.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">9. Limitation of Liability</h2>
            <p className="mt-3">
              Deleg8 Studio is provided &quot;as is&quot; without warranties of any kind. We are not liable for indirect, incidental, or consequential damages. Our total liability is limited to the amount you paid for the service in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">10. Termination</h2>
            <p className="mt-3">
              You may delete your account at any time. We may suspend or terminate accounts that violate these terms. Upon termination, your data will be deleted within 30 days unless legally required to retain it.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">11. Changes to Terms</h2>
            <p className="mt-3">
              We may update these terms from time to time. Material changes will be communicated via email or in-app notification. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">12. Contact</h2>
            <p className="mt-3">
              For questions about these terms, email us at{' '}
              <a href="mailto:legal@deleg8.dev" className="text-creative-400 hover:underline">
                legal@deleg8.dev
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
